import * as React from 'react';

import { Form, Row, Spin } from 'antd';
import {
  AimOutlined,
  Checkbox,
  Input,
  Notification,
  Space,
  Tooltip,
} from 'farmhub-ui-core';
import { useSelector } from 'react-redux';

import { RootState, useAppDispatch, useAppSelector } from 'app';
import { LocationSelectMobile, LocationSelectV1 } from 'components';
import {
  findLocationValue,
  findWithAuth,
  resetLocationSearch,
  selectAppDevice,
} from 'slice';
import { theme } from 'styled';
import { DEVICES } from 'utils';
import {
  getCurrentLocation,
  isVietnamesePhoneNumber,
  validateEmail,
} from 'utils/function';

import { CForm } from './styles';

interface InformationStepProps {
  form: any;
  hasErrorCode: boolean;
  isValidEmail: boolean;
  isValidPhoneNumber: boolean;
  setIsValidEmail: (e: boolean) => void;
  setHasErrorCode: (e: boolean) => void;
  setIsValidPhoneNumber: (e: boolean) => void;
}

const InformationStep = ({
  form,
  hasErrorCode,
  isValidEmail,
  isValidPhoneNumber,
  setHasErrorCode,
  setIsValidEmail,
  setIsValidPhoneNumber,
}: InformationStepProps): JSX.Element => {
  const appDevice = useAppSelector(selectAppDevice);
  const accountList = useAppSelector(state => state.account.list);
  const [isCentral, setIsCentral] = React.useState(false);
  const [isLoadingAddress, setIsLoadingAddress] = React.useState(false);
  const reloadAccountsFlag = useAppSelector(state => state.account.reloadFlag);
  const findLocation = useSelector(
    (state: RootState) => state.location.findLocation
  );
  const dispatch = useAppDispatch();

  const handleVerifyEmail = e => {
    setIsValidEmail(validateEmail(e.target.value));
  };

  const handleVerifyPhone = e => {
    setIsValidPhoneNumber(isVietnamesePhoneNumber(e.target.value));
  };

  const handleChangeIsLoadingAddress = (e: boolean): void => {
    setIsLoadingAddress(e);
  };

  const handleGetLocationCode = (value: any): void => {
    dispatch(
      findLocationValue({
        districtName: value.districtName,
        provinceName: value.provinceName,
        wardName: value.wardName,
        address: value.address,
        latitude: value.latitude,
        longitude: value.longitude,
      })
    );
  };

  React.useEffect(() => {
    const initAccountList = async () => {
      try {
        await dispatch(findWithAuth({ body: {}, query: {} })).unwrap();
      } catch (error: any) {
        Notification('error', 'Tải dữ liệu thất bại', error);
      }
    };

    if (accountList.length == 0) {
      initAccountList();
    }
  }, [dispatch, reloadAccountsFlag]);

  React.useEffect(() => {
    if (findLocation) {
      const bodyUpdate = {
        address: findLocation.address,
        provinceCode: findLocation.provinceCode,
        districtCode: findLocation.districtCode,
        wardCode: findLocation.wardCode,
        latitude: findLocation.latitude,
        longitude: findLocation.longitude,
      };
      form.setFieldsValue(bodyUpdate);
      setIsLoadingAddress(false);
      dispatch(resetLocationSearch());
    }
  }, [dispatch, findLocation, form]);

  return (
    <>
      <CForm
        className="login-form"
        form={form}
        layout="vertical"
        name="add_address_form"
        style={{ width: appDevice.device === DEVICES.DESKTOP ? 420 : '100%' }}
      >
        <Form.Item
          label="Tên nông trại"
          name="fullName"
          rules={[
            {
              required: true,
              message: 'Bạn chưa nhập tên!',
            },
          ]}
        >
          <Input height={theme.input.height.md} placeholder="Tên nông trại" />
        </Form.Item>
        <Form.Item
          help={
            isValidEmail ? null : (
              <span style={{ color: 'red' }}>Email không hợp lệ</span>
            )
          }
          label="Email"
          name="email"
          rules={[
            {
              required: true,
              message: 'Bạn chưa nhập email!',
            },
          ]}
        >
          <Input
            height={theme.input.height.md}
            placeholder="Email"
            onChange={handleVerifyEmail}
          />
        </Form.Item>
        <Form.Item
          help={
            isValidPhoneNumber ? null : (
              <span style={{ color: 'red' }}>Số điện thoại không hợp lệ!</span>
            )
          }
          label="Số điện thoại"
          name="phoneNumber"
          rules={[
            {
              required: true,
              message: 'Bạn chưa nhập số điện thoại!',
            },
          ]}
        >
          <Input
            height={theme.input.height.md}
            placeholder="Số điện thoại"
            onChange={handleVerifyPhone}
          />
        </Form.Item>
        <Row gutter={[0, 0]}>
          {appDevice.device === DEVICES.DESKTOP ? (
            <LocationSelectV1
              required
              districtCodeInit={form.getFieldValue('districtCode')}
              form={form}
              hasErrorCode={hasErrorCode}
              idPopup="normal_login"
              isLoadingAddress={isLoadingAddress}
              marginBottom={0}
              provinceCodeInit={form.getFieldValue('provinceCode')}
              setHasErrorCode={setHasErrorCode}
              showTitle={false}
              wardCodeInit={form.getFieldValue('wardCode')}
            />
          ) : (
            <>
              <LocationSelectMobile
                districtCodeInit={form.getFieldValue('districtCode')}
                form={form}
                hasErrorCode={hasErrorCode}
                idPopup="normal_login"
                isLoadingAddress={isLoadingAddress}
                isNotShowLabel={false}
                marginBottom={0}
                provinceCodeInit={form.getFieldValue('provinceCode')}
                setHasErrorCode={setHasErrorCode}
                wardCodeInit={form.getFieldValue('wardCode')}
              />
              <Space height={18} />
            </>
          )}
        </Row>
        <Form.Item
          label="Địa chỉ"
          name="address"
          rules={[
            {
              required: true,
              message: 'Bạn chưa nhập địa chỉ!',
            },
          ]}
        >
          <Input
            height={theme.input.height.md}
            placeholder="Địa chỉ"
            suffix={
              <Tooltip title="Lấy vị trí hiện tại">
                {!isLoadingAddress ? (
                  <AimOutlined
                    style={{
                      color: theme.colors.primary.main,
                      fontSize: '20px',
                    }}
                    onClick={() =>
                      getCurrentLocation(
                        handleChangeIsLoadingAddress,
                        handleGetLocationCode
                      )
                    }
                  />
                ) : (
                  <Spin
                    style={{
                      color: theme.colors.primary.main,
                      fontSize: '20px',
                    }}
                  />
                )}
              </Tooltip>
            }
          />
        </Form.Item>
        {accountList.length === 0 && (
          <Form.Item
            initialValue={isCentral}
            name="isCentral"
            style={{
              width: '100%',
              marginBottom: 0,
            }}
            valuePropName="checked"
          >
            <Checkbox
              checked={isCentral}
              styles={{ fontSize: 18 }}
              onChange={() => setIsCentral(!isCentral)}
            >
              Đặt làm trung tâm
            </Checkbox>
          </Form.Item>
        )}
      </CForm>
    </>
  );
};

export default InformationStep;
